import Big from 'big.js'
import { Container, BitmapText, Text, Sprite, Texture } from 'pixi.js'
import { SymbolType, multiplierSymbol } from '../enum/SymbolType'
import { formatToCommaWithLang, getRandomIntBetween, toCommaOptions } from '@slot-nx/core'
import { Spine } from '@esotericsoftware/spine-pixi'
import gsap from 'gsap'
import { SymbolData } from '../model/SymbolData'
import { ContainNameType } from '../enum/ContainNameType'
import { gameConfig } from '../../../config'

export const formatNumberWithComma = (num: number, option = toCommaOptions.maximumTwoDecimalPlaces): string => {
  return formatToCommaWithLang(num, navigator.language, option)
}

export function changeNode(symbol: Container, data: SymbolData) {
  const symbolImage = symbol.getChildByName<Sprite>(ContainNameType.SymbolImage) as Sprite
  const mulpitlierNumber = symbol.getChildByName<BitmapText>(ContainNameType.SymbolMulpitlierNumber) as BitmapText
  const key = getSymbolImg(data)
  symbolImage.texture = Texture.from(key)
  if (data.type === SymbolType.S1) mulpitlierNumber.text = String(`${data.multiplier}X`)
  else mulpitlierNumber.text = String('')
}

export const changeFontSize = (text: string): number => {
  if (text.length > 9) {
    return 40
  }
  if (text.length > 7) {
    return 42
  }
  return 44
}

export const playEventSpine = (spine: Spine, animation: string, loop: boolean) => {
  const trackEntry = spine.state.setAnimation(0, animation, loop)
  const eventTask = new Promise<void>((resolve) => {
    const clearCb = () => {
      spine.state.clearListeners()
    }
    spine.state.addListener({
      event() {
        clearCb()
        resolve()
      },
    })
  })

  const spineTask = new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, trackEntry.animation?.duration as number * 1000)
  })

  return [eventTask, spineTask]
}

export const playEventSpineTimes = (spine: Spine, animation: string, loop: boolean, times = 1): Promise<void> => {
  return new Promise<void>((resolve) => {
    let playCount = 0

    const playOnce = () => {
      const trackEntry = spine.state.setAnimation(0, animation, loop)

      // Fallback resolve by duration
      const durationFallback = setTimeout(() => {
        handleNext()
      }, (trackEntry.animation?.duration || 0) * 1000)

      const handleNext = () => {
        clearTimeout(durationFallback)
        spine.state.clearListeners()
        playCount++

        if (playCount >= times) {
          resolve()
        }
        else {
          playOnce() // 撥放下一次
        }
      }

      spine.state.clearListeners() // 清 listener 避免重疊
      spine.state.addListener({
        event() {
          handleNext()
        },
      })
    }

    playOnce() // 撥放第一次
  })
}

export function getRandomNormalSymbol(): SymbolData {
  return {
    type: getRandomIntBetween(1, 10) as SymbolType,
    multiplier: 0,
  }
}

export function preciseAdd(num1: number, num2: number, decimal = 2): number {
  const n1 = new Big(num1)
  const n2 = new Big(num2)
  return n1.plus(n2).round(decimal, Big.roundDown).toNumber()
}

export const numberAnimation = (
  startNum: number,
  endNum: number,
  duration: number,
  node: BitmapText | Text,
  format = toCommaOptions.forceTwoDecimalPlaces,
): [Promise<void>, () => void] => {
  const displayNum = { value: startNum }
  let isSkip = false
  const numberNode = node as BitmapText
  const commaOptions = format || toCommaOptions.forceTwoDecimalPlaces
  const anim = new Promise<void>((resolve) => {
    const task = gsap.to(displayNum, {
      value: endNum,
      duration: duration,
      onUpdate: () => {
        if (isSkip) {
          numberNode.text = formatToCommaWithLang(endNum, navigator.language, commaOptions)
          resolve()
          task.kill()
          return
        }
        numberNode.text = formatToCommaWithLang(displayNum.value, navigator.language, commaOptions)
      },
      onComplete: () => {
        resolve()
      },
    })
  })
  return [
    anim,
    () => {
      isSkip = true
    },
  ]
}

export function getSymbolImg(data: SymbolData): string {
  if (data.type === SymbolType.S1) { // Use the enum member
    const m = data.multiplier
    if (m < multiplierSymbol.M1) {
      return 'Multiplier_Symbol_lv1'
    }
    else if (m < multiplierSymbol.M2) {
      return 'Multiplier_Symbol_lv2'
    }
    else if (m < multiplierSymbol.M3) {
      return 'Multiplier_Symbol_lv3'
    }
    else if (m < multiplierSymbol.M4) {
      return 'Multiplier_Symbol_lv4'
    }
  }
  return SymbolType[data.type]
}

export function getMultiplierLv(multiplier: number): string {
  if (multiplier < multiplierSymbol.M1) {
    return 'Multiplier1'
  }
  else if (multiplier < multiplierSymbol.M2) {
    return 'Multiplier2'
  }
  else if (multiplier < multiplierSymbol.M3) {
    return 'Multiplier3'
  }
  else {
    return 'Multiplier4'
  }
}

export const numberAnimationV2 = (startNum: number, endNum: number, duration: number, component: BitmapText | Text, option = toCommaOptions.forceTwoDecimalPlaces, extendText?: string): [Promise<void>, () => void] => {
  const displayNum = { value: startNum }
  let isSkip = false
  const anim = new Promise<void>((resolve) => {
    const task = gsap.to(displayNum, {
      value: endNum,
      duration: duration,
      onUpdate: () => {
        if (isSkip) {
          const finalText = formatToCommaWithLang(endNum, navigator.language, option)
          component.text = finalText + (extendText ? extendText : '')
          resolve()
          task.kill()
          return
        }
        const currentText = formatToCommaWithLang(displayNum.value, navigator.language, {
          maximumFractionDigits: option?.maximumFractionDigits ?? 2,
          minimumFractionDigits: option?.minimumFractionDigits ?? 2,
        })
        component.text = currentText + (extendText ? extendText : '')
      },
      onComplete: () => {
        const finalText = formatToCommaWithLang(endNum, navigator.language, option)
        component.text = finalText + (extendText ? extendText : '')
        resolve()
      },
    })
  })

  return [anim, () => (isSkip = true)]
}

export function createSymbolContainer(symbolData: { type: SymbolType, multiplier: number }): Container {
  const container = new Container()
  const textureName = getSymbolImg(symbolData)
  const sprite = Sprite.from(textureName)
  sprite.name = ContainNameType.SymbolImage
  sprite.anchor.set(0.5)
  sprite.x = gameConfig.nodeWidth / 2
  sprite.y = gameConfig.nodeHeight / 2
  container.addChild(sprite)
  const isMultiplierSymbol = symbolData.type === SymbolType.S1
  const multiplierValue = isMultiplierSymbol ? `${symbolData.multiplier}X` : ''
  const multiplierText = new BitmapText(multiplierValue, { fontName: 'fontB', fontSize: 20 })
  multiplierText.name = ContainNameType.SymbolMulpitlierNumber
  multiplierText.anchor.set(0.5)
  multiplierText.x = gameConfig.nodeWidth / 2 + 2
  multiplierText.y = gameConfig.nodeHeight / 2 - 6
  container.addChild(multiplierText)
  return container
}

export function translatePotSkine(type: number) {
  switch (type) {
    case 66:
      return `CollectionR`
    case 77:
      return `CollectionB`
    case 88:
      return `CollectionG`
    default:
      return ''
  }
}
